import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowClockwise,
  CalendarCheck,
  CaretLeft,
  CaretRight,
  CheckCircle,
  Clock,
  Eye,
  Funnel,
  Gauge,
  Pause,
  PencilSimple,
  Play,
  Plus,
  WarningCircle,
  Wrench,
} from "@phosphor-icons/react";

import {
  Link,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";

import { gmao } from "@/api";
import MachineMeterModal from "@/components/gmao/MachineMeterModal";
import MaintenancePlanModal from "@/components/gmao/MaintenancePlanModal";
import DesktopPageHeader from "@/components/layout/DesktopPageHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { hasPageAccess } from "@/lib/accessPermissions";


const STATUS_LABELS = {
  overdue: "En retard",
  due_soon: "À venir",
  intervention_open: "En cours",
  ok: "À jour",
  inactive: "Inactif",
  unscheduled: "À planifier",
};


const PRIORITY_LABELS = {
  low: "Basse",
  normal: "Moyenne",
  high: "Haute",
  urgent: "Critique",
};


const PRIORITY_CLASSES = {
  low:
    "bg-[var(--do-success-soft)] text-[var(--do-ink)]",
  normal:
    "bg-[var(--do-warning-soft)] text-[var(--do-ink)]",
  high:
    "bg-[var(--do-danger-soft)] text-[var(--do-ink)]",
  urgent:
    "bg-[var(--do-danger-soft)] text-[var(--do-ink)]",
};


const STATUS_CLASSES = {
  overdue:
    "bg-[var(--do-danger-soft)] text-[var(--do-ink)] border-[var(--do-danger)]",
  due_soon:
    "bg-[var(--do-info-soft)] text-[var(--do-ink)] border-[var(--do-info)]",
  intervention_open:
    "bg-[var(--do-warning-soft)] text-[var(--do-ink)] border-[var(--do-warning)]",
  ok:
    "bg-[var(--do-success-soft)] text-[var(--do-ink)] border-[var(--do-success)]",
  inactive:
    "bg-[var(--do-bg-2)] text-[var(--do-ink)] border-[var(--do-border)]",
  unscheduled:
    "bg-[var(--do-bg-2)] text-[var(--do-ink)] border-[var(--do-border)]",
};


const UNIT_LABELS = {
  day: "jour(s)",
  week: "semaine(s)",
  month: "mois",
  year: "année(s)",
  hour: "heure(s)",
};


const PAGE_SIZE_OPTIONS = [
  5,
  10,
  20,
];


function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }
  );
}


function isCompletedThisMonth(plan) {
  if (!plan.lastMaintenanceAt) {
    return false;
  }

  const value = new Date(
    plan.lastMaintenanceAt
  );

  if (Number.isNaN(value.getTime())) {
    return false;
  }

  const now = new Date();

  return (
    value.getFullYear()
      === now.getFullYear()
    && value.getMonth()
      === now.getMonth()
  );
}


function dueLabel(plan) {
  if (
    plan.scheduleType ===
    "meter"
  ) {
    return (
      `${plan.nextDueMeterHours ?? "—"} h`
    );
  }

  return formatDate(
    plan.nextDueAt
  );
}


function remainingLabel(plan) {
  if (
    plan.status ===
    "intervention_open"
  ) {
    return "Intervention ouverte";
  }

  const value =
    plan.scheduleType === "meter"
      ? plan.remainingHours
      : plan.remainingDays;

  if (
    value === null
    || value === undefined
  ) {
    return "";
  }

  const unit =
    plan.scheduleType === "meter"
      ? "h"
      : "j";

  if (value < 0) {
    return (
      `En retard de ${Math.abs(
        Math.round(value)
      )} ${unit}`
    );
  }

  return (
    `Dans ${Math.round(value)} ${unit}`
  );
}


function maintenanceProgress(plan) {
  if (
    plan.status === "overdue"
  ) {
    return 100;
  }

  if (
    plan.status ===
    "intervention_open"
  ) {
    return 100;
  }

  const remaining =
    plan.scheduleType === "meter"
      ? plan.remainingHours
      : plan.remainingDays;

  const warning =
    Number(plan.warningValue) || 0;

  if (
    remaining === null
    || remaining === undefined
    || warning <= 0
  ) {
    return 0;
  }

  if (remaining > warning) {
    return 18;
  }

  return Math.max(
    8,
    Math.min(
      100,
      Math.round(
        ((warning - remaining)
          / warning)
        * 100
      )
    )
  );
}


function metricPercent(
  value,
  total
) {
  if (!total) {
    return 0;
  }

  return Math.round(
    (value / total) * 100
  );
}


function planMatchesVirtualStatus(
  plan,
  status
) {
  if (status === "all") {
    return true;
  }

  if (status === "completed") {
    return isCompletedThisMonth(
      plan
    );
  }

  return plan.status === status;
}


function MaintenanceMetric({
  label,
  value,
  percent,
  detail,
  icon: Icon,
  active,
  onClick,
  testId,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={[
        "min-w-0 rounded-2xl border p-3 text-left transition",
        "bg-[var(--do-bg-solid)]",
        active
          ? "border-[var(--do-accent)] shadow-sm ring-1 ring-[var(--do-accent)]"
          : "border-[var(--do-border)] hover:border-[var(--do-accent)]",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
          <Icon
            size={18}
            weight="duotone"
          />
        </span>

        <span className="text-[10px] font-bold text-[var(--do-muted)]">
          {percent}%
        </span>
      </div>

      <div className="mt-2 text-[11px] font-semibold text-[var(--do-muted)]">
        {label}
      </div>

      <div className="mt-0.5 font-heading text-xl font-black text-[var(--do-ink)]">
        {value}
      </div>

      <div className="mt-1 hidden text-[10px] text-[var(--do-muted)] sm:block">
        {detail}
      </div>
    </button>
  );
}


function StatusBadge({
  status,
}) {
  return (
    <span
      className={[
        "inline-flex rounded-full border px-2 py-1 text-[11px] font-bold",
        STATUS_CLASSES[status]
          || STATUS_CLASSES.unscheduled,
      ].join(" ")}
    >
      {STATUS_LABELS[status]
        || status}
    </span>
  );
}


function PriorityBadge({
  priority,
}) {
  const key =
    priority || "normal";

  return (
    <span
      className={[
        "inline-flex rounded-full px-2 py-1 text-[11px] font-bold",
        PRIORITY_CLASSES[key]
          || PRIORITY_CLASSES.normal,
      ].join(" ")}
    >
      {PRIORITY_LABELS[key]
        || key}
    </span>
  );
}


function ProgressBar({
  plan,
}) {
  const progress =
    maintenanceProgress(plan);

  const isDanger =
    plan.status === "overdue";

  return (
    <div className="mt-1.5">
      <div className="h-1.5 overflow-hidden rounded-full bg-[var(--do-bg-2)]">
        <div
          className={[
            "h-full rounded-full transition-all",
            isDanger
              ? "bg-[var(--do-danger)]"
              : "bg-[var(--do-success)]",
          ].join(" ")}
          style={{
            width: `${progress}%`,
          }}
        />
      </div>

      <div
        className={[
          "mt-1 text-[10px] font-semibold",
          "text-[var(--do-ink)]",
        ].join(" ")}
      >
        {remainingLabel(plan)}
      </div>
    </div>
  );
}


export default function GmaoMaintenance() {
  const { user } = useAuth();

  const [searchParams] =
    useSearchParams();

  const machineFilter =
    searchParams.get(
      "machine_id"
    ) || "";

  const canManage = [
    "admin",
    "manager",
    "maintenance_manager",
  ].includes(user?.role);

  const canViewMachines =
    hasPageAccess(
      user,
      "gmao",
      "machines"
    );

  const canViewInterventions =
    hasPageAccess(
      user,
      "gmao",
      "interventions"
    );

  const canManagePlans =
    canManage
    && canViewMachines;

  const canUpdateMeter = [
    "admin",
    "manager",
    "maintenance_manager",
    "technician",
  ].includes(user?.role)
    && canViewMachines;

  const [plans, setPlans] =
    useState([]);

  const [machines, setMachines] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [search, setSearch] =
    useState("");

  const [
    technicianFilter,
    setTechnicianFilter,
  ] = useState("all");

  const [
    priorityFilter,
    setPriorityFilter,
  ] = useState("all");

  const [
    periodFilter,
    setPeriodFilter,
  ] = useState("all");

  const [
    advancedOpen,
    setAdvancedOpen,
  ] = useState(false);

  const [page, setPage] =
    useState(1);

  const [
    pageSize,
    setPageSize,
  ] = useState(5);

  const [editorOpen, setEditorOpen] =
    useState(false);

  const [
    editingPlan,
    setEditingPlan,
  ] = useState(null);

  const [meterOpen, setMeterOpen] =
    useState(false);


  const load = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const [
          planData,
          machineData,
        ] = await Promise.all([
          gmao.listMaintenancePlans({
            machine_id:
              machineFilter,
            include_inactive:
              canManagePlans,
            limit: 1000,
          }),
          (
            canManagePlans
            || canUpdateMeter
          )
            ? gmao.listMachines({
                limit: 500,
              })
            : Promise.resolve([]),
        ]);

        setPlans(
          Array.isArray(planData)
            ? planData
            : []
        );

        setMachines(
          Array.isArray(machineData)
            ? machineData
            : []
        );
      } catch (requestError) {
        const message =
          formatApiError(
            requestError
              ?.response
              ?.data
              ?.detail
          );

        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [
      canManagePlans,
      canUpdateMeter,
      machineFilter,
    ]
  );


  useEffect(() => {
    load();
  }, [load]);


  useEffect(() => {
    setPage(1);
  }, [
    search,
    statusFilter,
    technicianFilter,
    priorityFilter,
    periodFilter,
    pageSize,
  ]);


  const activePlans =
    useMemo(
      () =>
        plans.filter(
          (plan) =>
            plan.status !==
            "inactive"
        ),
      [plans]
    );


  const metrics =
    useMemo(() => {
      const total =
        activePlans.length;

      return {
        total,
        unscheduled:
          activePlans.filter(
            (plan) =>
              plan.status ===
              "unscheduled"
          ).length,
        dueSoon:
          activePlans.filter(
            (plan) =>
              plan.status ===
              "due_soon"
          ).length,
        open:
          activePlans.filter(
            (plan) =>
              plan.status ===
              "intervention_open"
          ).length,
        completed:
          plans.filter(
            isCompletedThisMonth
          ).length,
        overdue:
          activePlans.filter(
            (plan) =>
              plan.status ===
              "overdue"
          ).length,
      };
    }, [
      activePlans,
      plans,
    ]);


  const technicianOptions =
    useMemo(
      () =>
        Array.from(
          new Set(
            plans
              .map(
                (plan) =>
                  plan.technicianName
              )
              .filter(Boolean)
          )
        ).sort(
          (a, b) =>
            a.localeCompare(
              b,
              "fr"
            )
        ),
      [plans]
    );


  const filteredPlans =
    useMemo(() => {
      const needle =
        search
          .trim()
          .toLocaleLowerCase(
            "fr"
          );

      return plans.filter(
        (plan) => {
          if (
            !planMatchesVirtualStatus(
              plan,
              statusFilter
            )
          ) {
            return false;
          }

          if (
            technicianFilter
              !== "all"
            && plan.technicianName
              !== technicianFilter
          ) {
            return false;
          }

          if (
            priorityFilter
              !== "all"
            && (
              plan.priority
              || "normal"
            ) !== priorityFilter
          ) {
            return false;
          }

          if (
            periodFilter
              !== "all"
          ) {
            const remaining =
              plan.remainingDays;

            if (
              remaining === null
              || remaining
                === undefined
            ) {
              return false;
            }

            if (
              periodFilter === "7"
              && (
                remaining < 0
                || remaining > 7
              )
            ) {
              return false;
            }

            if (
              periodFilter === "30"
              && (
                remaining < 0
                || remaining > 30
              )
            ) {
              return false;
            }

            if (
              periodFilter === "90"
              && (
                remaining < 0
                || remaining > 90
              )
            ) {
              return false;
            }
          }

          if (!needle) {
            return true;
          }

          const haystack = [
            plan.name,
            plan.machineName,
            plan.machineSerialNumber,
            plan.machineSiteName,
            plan.machineEquipment,
            plan.technicianName,
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase(
              "fr"
            );

          return haystack.includes(
            needle
          );
        }
      );
    }, [
      periodFilter,
      plans,
      priorityFilter,
      search,
      statusFilter,
      technicianFilter,
    ]);


  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredPlans.length
        / pageSize
      )
    );


  const safePage =
    Math.min(
      page,
      totalPages
    );


  const pagePlans =
    useMemo(
      () =>
        filteredPlans.slice(
          (safePage - 1)
            * pageSize,
          safePage * pageSize
        ),
      [
        filteredPlans,
        pageSize,
        safePage,
      ]
    );


  const savePlan = async (
    payload
  ) => {
    setSaving(true);

    try {
      if (editingPlan) {
        await gmao
          .updateMaintenancePlan(
            editingPlan.id,
            payload
          );

        toast.success(
          "Plan de maintenance modifié"
        );
      } else {
        await gmao.createMaintenancePlan(
          payload
        );

        toast.success(
          "Plan de maintenance créé"
        );
      }

      setEditorOpen(false);
      setEditingPlan(null);

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  const updateMeter = async (
    machineId,
    payload
  ) => {
    setSaving(true);

    try {
      await gmao.updateMachineMeter(
        machineId,
        payload
      );

      toast.success(
        "Compteur machine mis à jour"
      );

      setMeterOpen(false);

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  const generate = async (
    plan
  ) => {
    if (
      !window.confirm(
        `Générer une intervention préventive pour « ${plan.name} » ?`
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      const intervention =
        await gmao.generateMaintenanceIntervention(
          plan.id,
          {}
        );

      toast.success(
        "Intervention préventive générée"
      );

      await load();

      window.location.assign(
        `/app/gmao/interventions/${encodeURIComponent(
          intervention.id
        )}`
      );
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        )
      );
    } finally {
      setBusy(false);
    }
  };


  const togglePlan = async (
    plan
  ) => {
    setBusy(true);

    try {
      if (
        plan.status ===
        "inactive"
      ) {
        await gmao
          .activateMaintenancePlan(
            plan.id
          );

        toast.success(
          "Plan réactivé"
        );
      } else {
        await gmao
          .deactivateMaintenancePlan(
            plan.id
          );

        toast.success(
          "Plan désactivé"
        );
      }

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError
            ?.response
            ?.data
            ?.detail
        )
      );
    } finally {
      setBusy(false);
    }
  };


  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTechnicianFilter("all");
    setPriorityFilter("all");
    setPeriodFilter("all");
    setPage(1);
  };


  if (loading) {
    return (
      <div
        className="p-4 sm:p-6 lg:p-8"
        data-testid="gmao-maintenance-loading"
      >
        <div className="label-eyebrow lirah-pulse">
          Chargement de la maintenance préventive
        </div>
      </div>
    );
  }


  if (error) {
    return (
      <div
        className="max-w-3xl p-4 sm:p-6 lg:p-8"
        data-testid="gmao-maintenance-error"
      >
        <div className="lirah-card p-8">
          <h1 className="font-heading text-xl font-black">
            Maintenance préventive indisponible
          </h1>

          <p className="mt-2 text-sm text-[var(--do-muted)]">
            {error}
          </p>

          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={load}
          >
            <ArrowClockwise />
            Réessayer
          </Button>
        </div>
      </div>
    );
  }


  return (
    <main
      className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8"
      data-testid="gmao-maintenance-page"
    >
      <DesktopPageHeader
        eyebrow="GMAO"
        title="Maintenance préventive"
        description="Pilotez les maintenances préventives de vos équipements."
        icon={Wrench}
        testId="gmao-maintenance-desktop-header"
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={busy}
              data-testid="refresh-maintenance-plans"
            >
              <ArrowClockwise />
              Actualiser
            </Button>

            {canManagePlans && (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setEditingPlan(null);
                  setEditorOpen(true);
                }}
                disabled={busy}
                data-testid="create-maintenance-plan"
              >
                <Plus />
                Planifier une maintenance
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4 flex items-start justify-between gap-3 md:hidden">
        <div>
          <div className="label-eyebrow">
            GMAO
          </div>

          <h1 className="mt-1 font-heading text-xl font-black">
            Maintenance préventive
          </h1>
        </div>

        {canManagePlans && (
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setEditingPlan(null);
              setEditorOpen(true);
            }}
            disabled={busy}
            data-testid="create-maintenance-plan-mobile"
          >
            <Plus />
            Planifier
          </Button>
        )}
      </div>

      {!canManagePlans && (
        <div className="mb-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm">
          Consultation uniquement. Les actions de gestion sont réservées aux responsables habilités.
        </div>
      )}

      <section
        className="grid grid-cols-[repeat(auto-fit,minmax(min(10rem,100%),1fr))] gap-1.5 sm:gap-3"
        data-testid="gmao-maintenance-status-metrics"
      >
        <MaintenanceMetric
          label="À planifier"
          value={metrics.unscheduled}
          percent={metricPercent(
            metrics.unscheduled,
            metrics.total
          )}
          detail="Dont critiques"
          icon={CalendarCheck}
          active={
            statusFilter
            === "unscheduled"
          }
          onClick={() =>
            setStatusFilter(
              statusFilter
                === "unscheduled"
                ? "all"
                : "unscheduled"
            )
          }
          testId="maintenance-metric-unscheduled"
        />

        <MaintenanceMetric
          label="À venir"
          value={metrics.dueSoon}
          percent={metricPercent(
            metrics.dueSoon,
            metrics.total
          )}
          detail="Prochains jours"
          icon={Clock}
          active={
            statusFilter
            === "due_soon"
          }
          onClick={() =>
            setStatusFilter(
              statusFilter
                === "due_soon"
                ? "all"
                : "due_soon"
            )
          }
          testId="maintenance-metric-due-soon"
        />

        <MaintenanceMetric
          label="En cours"
          value={metrics.open}
          percent={metricPercent(
            metrics.open,
            metrics.total
          )}
          detail="Interventions ouvertes"
          icon={Wrench}
          active={
            statusFilter
            === "intervention_open"
          }
          onClick={() =>
            setStatusFilter(
              statusFilter
                === "intervention_open"
                ? "all"
                : "intervention_open"
            )
          }
          testId="maintenance-metric-open"
        />

        <MaintenanceMetric
          label="Terminées"
          value={metrics.completed}
          percent={metricPercent(
            metrics.completed,
            metrics.total
          )}
          detail="Ce mois"
          icon={CheckCircle}
          active={
            statusFilter
            === "completed"
          }
          onClick={() =>
            setStatusFilter(
              statusFilter
                === "completed"
                ? "all"
                : "completed"
            )
          }
          testId="maintenance-metric-completed"
        />

        <MaintenanceMetric
          label="En retard"
          value={metrics.overdue}
          percent={metricPercent(
            metrics.overdue,
            metrics.total
          )}
          detail="À traiter"
          icon={WarningCircle}
          active={
            statusFilter
            === "overdue"
          }
          onClick={() =>
            setStatusFilter(
              statusFilter
                === "overdue"
                ? "all"
                : "overdue"
            )
          }
          testId="maintenance-metric-overdue"
        />
      </section>

      <section className="mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-[1_1_18rem]">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Rechercher machine, n° de série, site…"
              className="h-10 w-full rounded-xl border border-[var(--do-border)] bg-transparent px-3 text-sm outline-none focus:border-[var(--do-accent)]"
              data-testid="maintenance-search"
            />
          </div>

          <div className="flex gap-2 md:hidden">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() =>
                setAdvancedOpen(
                  (value) => !value
                )
              }
            >
              <Funnel />
              Filtres
            </Button>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              className="h-9 flex-1 rounded-md border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2 text-xs"
              aria-label="Filtrer par statut"
            >
              <option value="all">
                Tous
              </option>
              <option value="unscheduled">
                À planifier
              </option>
              <option value="due_soon">
                À venir
              </option>
              <option value="intervention_open">
                En cours
              </option>
              <option value="completed">
                Terminées
              </option>
              <option value="overdue">
                En retard
              </option>
              <option value="ok">
                À jour
              </option>
              <option value="inactive">
                Inactifs
              </option>
            </select>
          </div>

          <div className="hidden min-w-0 flex-[1_1_32rem] flex-wrap items-center gap-2 md:flex">
            <select
              value={periodFilter}
              onChange={(event) =>
                setPeriodFilter(
                  event.target.value
                )
              }
              className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-xs"
              data-testid="maintenance-period-filter"
            >
              <option value="all">
                Période · Toutes
              </option>
              <option value="7">
                Dans 7 jours
              </option>
              <option value="30">
                Dans 30 jours
              </option>
              <option value="90">
                Dans 90 jours
              </option>
            </select>

            <select
              value={technicianFilter}
              onChange={(event) =>
                setTechnicianFilter(
                  event.target.value
                )
              }
              className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-xs"
              data-testid="maintenance-technician-filter"
            >
              <option value="all">
                Technicien · Tous
              </option>

              {technicianOptions.map(
                (name) => (
                  <option
                    key={name}
                    value={name}
                  >
                    {name}
                  </option>
                )
              )}
            </select>

            <select
              value={priorityFilter}
              onChange={(event) =>
                setPriorityFilter(
                  event.target.value
                )
              }
              className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-xs"
              data-testid="maintenance-priority-filter"
            >
              <option value="all">
                Criticité · Toutes
              </option>
              <option value="low">
                Basse
              </option>
              <option value="normal">
                Moyenne
              </option>
              <option value="high">
                Haute
              </option>
              <option value="urgent">
                Critique
              </option>
            </select>

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-xs"
              data-testid="maintenance-status-filter"
            >
              <option value="all">
                Statut · Tous
              </option>
              <option value="unscheduled">
                À planifier
              </option>
              <option value="due_soon">
                À venir
              </option>
              <option value="intervention_open">
                En cours
              </option>
              <option value="completed">
                Terminées ce mois
              </option>
              <option value="overdue">
                En retard
              </option>
              <option value="ok">
                À jour
              </option>
              <option value="inactive">
                Inactifs
              </option>
            </select>

            <button
              type="button"
              onClick={resetFilters}
              className="px-2 text-xs font-semibold text-[var(--do-muted)] hover:text-[var(--do-ink)]"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {advancedOpen && (
          <div className="mt-3 grid grid-cols-1 gap-2 border-t border-[var(--do-border)] pt-3 md:hidden">
            <select
              value={periodFilter}
              onChange={(event) =>
                setPeriodFilter(
                  event.target.value
                )
              }
              className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
            >
              <option value="all">
                Toutes les périodes
              </option>
              <option value="7">
                Dans 7 jours
              </option>
              <option value="30">
                Dans 30 jours
              </option>
              <option value="90">
                Dans 90 jours
              </option>
            </select>

            <select
              value={technicianFilter}
              onChange={(event) =>
                setTechnicianFilter(
                  event.target.value
                )
              }
              className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
            >
              <option value="all">
                Tous les techniciens
              </option>

              {technicianOptions.map(
                (name) => (
                  <option
                    key={name}
                    value={name}
                  >
                    {name}
                  </option>
                )
              )}
            </select>

            <select
              value={priorityFilter}
              onChange={(event) =>
                setPriorityFilter(
                  event.target.value
                )
              }
              className="h-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
            >
              <option value="all">
                Toutes les criticités
              </option>
              <option value="low">
                Basse
              </option>
              <option value="normal">
                Moyenne
              </option>
              <option value="high">
                Haute
              </option>
              <option value="urgent">
                Critique
              </option>
            </select>

            <Button
              type="button"
              variant="outline"
              onClick={resetFilters}
            >
              Réinitialiser les filtres
            </Button>
          </div>
        )}
      </section>

      {machineFilter && (
        <div className="mt-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm">
          Affichage limité à la machine sélectionnée.{" "}
          <Link
            to="/app/gmao/maintenance"
            className="font-bold underline"
          >
            Voir toutes les maintenances
          </Link>
        </div>
      )}

      {canUpdateMeter && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() =>
              setMeterOpen(true)
            }
            disabled={
              busy
              || machines.length === 0
            }
            data-testid="open-machine-meter"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--do-muted)] hover:text-[var(--do-ink)] disabled:opacity-50"
          >
            <Gauge size={15} />
            Relever un compteur
          </button>
        </div>
      )}

      {filteredPlans.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-10 text-center">
          <CalendarCheck
            size={32}
            className="mx-auto text-[var(--do-muted)]"
          />

          <h2 className="mt-3 font-heading text-lg font-black">
            Aucune maintenance correspondante
          </h2>

          <p className="mt-2 text-sm text-[var(--do-muted)]">
            Modifiez les filtres ou créez un nouveau plan de maintenance.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 hidden overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] lg:block">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px] border-collapse text-left">
                <thead className="bg-[var(--do-bg-2)] text-[10px] font-bold uppercase tracking-wide text-[var(--do-muted)]">
                  <tr>
                    <th className="px-3 py-3">
                      Machine / Site
                    </th>
                    <th className="px-3 py-3">
                      N° de série
                    </th>
                    <th className="px-3 py-3">
                      Équipement
                    </th>
                    <th className="px-3 py-3">
                      Prochaine maintenance
                    </th>
                    <th className="px-3 py-3">
                      Périodicité
                    </th>
                    <th className="px-3 py-3">
                      Criticité
                    </th>
                    <th className="px-3 py-3">
                      Technicien
                    </th>
                    <th className="px-3 py-3">
                      Statut
                    </th>
                    <th className="px-3 py-3">
                      Dernière maintenance
                    </th>
                    <th className="px-3 py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pagePlans.map(
                    (plan) => (
                      <tr
                        key={plan.id}
                        data-testid={`maintenance-plan-${plan.id}`}
                        className={[
                          "border-t border-[var(--do-border)] align-top",
                          plan.status
                            === "overdue"
                            ? "bg-[var(--do-danger-soft)]"
                            : "",
                        ].join(" ")}
                      >
                        <td className="px-3 py-3">
                          <div className="font-heading text-sm font-black">
                            {plan.machineName}
                          </div>

                          <div className="mt-0.5 text-[11px] text-[var(--do-muted)]">
                            {plan.machineSiteName
                              || "Site non renseigné"}
                          </div>
                        </td>

                        <td className="px-3 py-3 font-mono-stat text-xs">
                          {plan.machineSerialNumber
                            || "—"}
                        </td>

                        <td className="px-3 py-3 text-xs">
                          {plan.machineEquipment
                            || "—"}
                        </td>

                        <td className="px-3 py-3">
                          <div
                            className={[
                              "text-xs font-bold",
                              "text-[var(--do-ink)]",
                            ].join(" ")}
                          >
                            {dueLabel(plan)}
                          </div>

                          <ProgressBar
                            plan={plan}
                          />
                        </td>

                        <td className="px-3 py-3 text-xs font-semibold">
                          Tous les{" "}
                          {plan.intervalValue}{" "}
                          {UNIT_LABELS[
                            plan.intervalUnit
                          ] || plan.intervalUnit}
                        </td>

                        <td className="px-3 py-3">
                          <PriorityBadge
                            priority={
                              plan.priority
                            }
                          />
                        </td>

                        <td className="px-3 py-3 text-xs">
                          {plan.technicianName
                            || "Non affecté"}
                        </td>

                        <td className="px-3 py-3">
                          <StatusBadge
                            status={
                              plan.status
                            }
                          />
                        </td>

                        <td className="px-3 py-3 text-xs">
                          {formatDate(
                            plan.lastMaintenanceAt
                          )}
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-1">
                            {canViewMachines && (
                              <Button
                                asChild
                                variant="ghost"
                                size="icon"
                                title="Voir la machine"
                              >
                                <Link
                                  to={`/app/gmao/machines/${encodeURIComponent(
                                    plan.machineId
                                  )}`}
                                >
                                  <Eye />
                                </Link>
                              </Button>
                            )}

                            {canManagePlans && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                title="Modifier le plan"
                                onClick={() => {
                                  setEditingPlan(
                                    plan
                                  );
                                  setEditorOpen(
                                    true
                                  );
                                }}
                                disabled={busy}
                                data-testid={`edit-maintenance-plan-${plan.id}`}
                              >
                                <PencilSimple />
                              </Button>
                            )}

                            {canManagePlans
                              && canViewInterventions
                              && plan.status
                                !== "inactive"
                              && plan.status
                                !== "intervention_open" && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  title="Planifier l’intervention"
                                  onClick={() =>
                                    generate(
                                      plan
                                    )
                                  }
                                  disabled={busy}
                                  data-testid={`generate-maintenance-${plan.id}`}
                                >
                                  <CalendarCheck />
                                </Button>
                              )}

                            {canManagePlans && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                title={
                                  plan.status
                                    === "inactive"
                                    ? "Réactiver"
                                    : "Désactiver"
                                }
                                onClick={() =>
                                  togglePlan(
                                    plan
                                  )
                                }
                                disabled={busy}
                                data-testid={`toggle-maintenance-plan-${plan.id}`}
                              >
                                {plan.status
                                  === "inactive" ? (
                                  <Play />
                                ) : (
                                  <Pause />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 space-y-3 lg:hidden">
            {pagePlans.map(
              (plan) => (
                <article
                  key={plan.id}
                  data-testid={`maintenance-plan-mobile-${plan.id}`}
                  className={[
                    "rounded-2xl border bg-[var(--do-bg-solid)] p-4",
                    plan.status
                      === "overdue"
                      ? "border-[var(--do-danger)] bg-[var(--do-danger-soft)]"
                      : "border-[var(--do-border)]",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-heading text-sm font-black">
                        {plan.machineName}
                      </div>

                      <div className="mt-0.5 truncate text-xs text-[var(--do-muted)]">
                        {plan.machineSiteName
                          || "Site non renseigné"}
                      </div>

                      <div className="mt-1 font-mono-stat text-[10px] text-[var(--do-muted)]">
                        {plan.machineSerialNumber
                          || "—"}
                      </div>
                    </div>

                    <div className="shrink-0 text-right">
                      <div
                        className={[
                          "text-xs font-black",
                          "text-[var(--do-ink)]",
                        ].join(" ")}
                      >
                        {dueLabel(plan)}
                      </div>

                      <div className="mt-1">
                        <StatusBadge
                          status={plan.status}
                        />
                      </div>
                    </div>
                  </div>

                  <ProgressBar
                    plan={plan}
                  />

                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-[var(--do-border)] pt-3">
                    <div className="min-w-0 text-[11px] text-[var(--do-muted)]">
                      <div>
                        Tous les{" "}
                        {plan.intervalValue}{" "}
                        {UNIT_LABELS[
                          plan.intervalUnit
                        ] || plan.intervalUnit}
                      </div>

                      <div className="mt-0.5">
                        {plan.technicianName
                          || "Technicien non affecté"}
                      </div>
                    </div>

                    <div className="flex shrink-0 gap-1">
                      {canViewMachines && (
                        <Button
                          asChild
                          variant="outline"
                          size="icon"
                          title="Voir la machine"
                        >
                          <Link
                            to={`/app/gmao/machines/${encodeURIComponent(
                              plan.machineId
                            )}`}
                          >
                            <Eye />
                          </Link>
                        </Button>
                      )}

                      {canManagePlans
                        && canViewInterventions
                        && plan.status
                          !== "inactive"
                        && plan.status
                          !== "intervention_open" && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              generate(plan)
                            }
                            disabled={busy}
                            data-testid={`generate-maintenance-mobile-${plan.id}`}
                          >
                            <CalendarCheck />
                            Planifier
                          </Button>
                        )}
                    </div>
                  </div>
                </article>
              )
            )}
          </div>

          <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-[var(--do-muted)]">
              Affichage{" "}
              {filteredPlans.length
                ? (
                  (safePage - 1)
                  * pageSize
                ) + 1
                : 0}
              {" à "}
              {Math.min(
                safePage * pageSize,
                filteredPlans.length
              )}
              {" sur "}
              {filteredPlans.length}
              {" maintenance"}
              {filteredPlans.length > 1
                ? "s"
                : ""}
            </div>

            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={
                  safePage <= 1
                }
                onClick={() =>
                  setPage(
                    Math.max(
                      1,
                      safePage - 1
                    )
                  )
                }
                aria-label="Page précédente"
              >
                <CaretLeft />
              </Button>

              <div className="min-w-16 text-center text-xs font-bold">
                {safePage} / {totalPages}
              </div>

              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={
                  safePage
                  >= totalPages
                }
                onClick={() =>
                  setPage(
                    Math.min(
                      totalPages,
                      safePage + 1
                    )
                  )
                }
                aria-label="Page suivante"
              >
                <CaretRight />
              </Button>

              <select
                value={pageSize}
                onChange={(event) =>
                  setPageSize(
                    Number(
                      event.target.value
                    )
                  )
                }
                className="h-9 rounded-md border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2 text-xs"
                aria-label="Nombre de maintenances par page"
              >
                {PAGE_SIZE_OPTIONS.map(
                  (value) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {value} / page
                    </option>
                  )
                )}
              </select>
            </div>
          </div>
        </>
      )}

      <MaintenancePlanModal
        open={editorOpen}
        plan={editingPlan}
        machines={machines}
        initialMachineId={
          machineFilter
        }
        saving={saving}
        onClose={() => {
          setEditorOpen(false);
          setEditingPlan(null);
        }}
        onSubmit={savePlan}
      />

      <MachineMeterModal
        open={meterOpen}
        machines={machines}
        initialMachineId={
          machineFilter
        }
        saving={saving}
        onClose={() =>
          setMeterOpen(false)
        }
        onSubmit={updateMeter}
      />
    </main>
  );
}
